import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const PayRateCard = ({ employeeData, onRequestAdjustment }) => {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [whitelistLoading, setWhitelistLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState(false);
  const [requestStats, setRequestStats] = useState(null);
  const [expandHistory, setExpandHistory] = useState(false);
  const [hasRejectedRequest, setHasRejectedRequest] = useState(false);
  const [lastRateAdjustmentDate, setLastRateAdjustmentDate] = useState(null);

  useEffect(() => {
    if (employeeData?.employee_number) {
      fetchPayRates();
      checkWhitelistStatus();
      fetchRequestStats();
      checkRejectedRequests();
      fetchLastRateAdjustmentDate();
    }
  }, [employeeData]);

  const fetchPayRates = async () => {
    try {
      setLoading(true);
      
      // Fetch current and historical rates
      const { data, error } = await supabase
        .from('employee_pay_rates')
        .select('*')
        .eq('employee_number', employeeData.employee_number)
        .order('effective_date', { ascending: false });
      
      if (error) {
        console.error('Error fetching pay rates:', error);
        return;
      }
      
      setRates(data || []);
    } catch (error) {
      console.error('Error in fetchPayRates:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkWhitelistStatus = async () => {
    try {
      setWhitelistLoading(true);
      const { data, error } = await supabase
        .from('pay_rate_adjustment_whitelist')
        .select('enabled, max_requests_per_year, requests_this_year')
        .eq('employee_number', employeeData.employee_number)
        .single();
      
      if (error) {
        console.log('Employee not whitelisted:', error);
        setIsWhitelisted(false);
        return;
      }
      
      // Check if enabled and hasn't exceeded request limit
      // Also allow if user has rejected requests (can edit and resubmit)
      const canRequest = data?.enabled && 
        (data.max_requests_per_year === null || data.requests_this_year < data.max_requests_per_year);
      
      setIsWhitelisted(canRequest);
      
      // If user has rejected requests, they should be able to see the button
      // This will be checked separately in checkRejectedRequests
    } catch (error) {
      console.error('Error checking whitelist:', error);
      setIsWhitelisted(false);
    } finally {
      setWhitelistLoading(false);
    }
  };

  const fetchRequestStats = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_rate_request_stats', { emp_number: employeeData.employee_number });
      
      if (!error && data && data.length > 0) {
        setRequestStats(data[0]);
      }
    } catch (error) {
      console.error('Error fetching request stats:', error);
    }
  };

  const checkRejectedRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('pay_rate_adjustment_requests')
        .select('id')
        .eq('employee_number', employeeData.employee_number)
        .eq('status', 'rejected')
        .limit(1);
      
      if (!error && data && data.length > 0) {
        setHasRejectedRequest(true);
      } else {
        setHasRejectedRequest(false);
      }
    } catch (error) {
      console.error('Error checking rejected requests:', error);
      setHasRejectedRequest(false);
    }
  };

  const fetchLastRateAdjustmentDate = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('last_rate_adjustment_date')
        .eq('employee_number', employeeData.employee_number)
        .single();
      
      if (!error && data?.last_rate_adjustment_date) {
        setLastRateAdjustmentDate(data.last_rate_adjustment_date);
      } else {
        // Fallback: get from most recent rate if employees table doesn't have it
        const { data: ratesData, error: ratesError } = await supabase
          .from('employee_pay_rates')
          .select('effective_date, created_at')
          .eq('employee_number', employeeData.employee_number)
          .order('effective_date', { ascending: false })
          .order('created_at', { ascending: false })
          .limit(1);
        
        if (!ratesError && ratesData && ratesData.length > 0) {
          const rate = ratesData[0];
          setLastRateAdjustmentDate(rate.effective_date || rate.created_at);
        } else {
          setLastRateAdjustmentDate(null);
        }
      }
    } catch (error) {
      console.error('Error fetching last rate adjustment date:', error);
      setLastRateAdjustmentDate(null);
    }
  };

  const formatCurrency = (amount) => {
    return `$${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const currentRates = rates.filter(r => r.end_date === null);
  const historicalRates = rates.filter(r => r.end_date !== null);

  // Don't render anything if not whitelisted and no rejected requests (wait for checks to complete)
  if (!whitelistLoading && !isWhitelisted && !hasRejectedRequest) {
    return null;
  }

  if (loading || whitelistLoading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-6 animate-pulse">
        <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (currentRates.length === 0) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg shadow-lg p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="text-3xl">💰</span>
          <h2 className="text-2xl font-bold text-gray-800">Your Pay Rates</h2>
        </div>
        {requestStats && requestStats.pending_requests > 0 && (
          <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
            {requestStats.pending_requests} Pending
          </div>
        )}
      </div>

      {/* Current Rates */}
      <div className="space-y-4 mb-6">
        {currentRates.map((rate) => (
          <div key={rate.id} className="bg-white rounded-lg p-5 shadow-md">
            <div className="flex justify-between items-start mb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Company: {rate.company}</h3>
                <p className="text-sm text-gray-500">
                  {lastRateAdjustmentDate 
                    ? `Last rate adjustment: ${formatDate(lastRateAdjustmentDate)}`
                    : `Effective since ${formatDate(rate.effective_date)}`}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Check Rate */}
              <div className="border-l-4 border-blue-500 pl-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Check Rate</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(rate.check_rate)}/hr</p>
              </div>
              
              {/* Weekday Cash Rate */}
              <div className="border-l-4 border-green-500 pl-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Weekday Cash</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(rate.weekday_cash_rate)}/hr</p>
              </div>
              
              {/* Weekend Cash Rate */}
              <div className="border-l-4 border-purple-500 pl-3">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Weekend Cash</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(rate.weekend_cash_rate)}/hr</p>
              </div>
            </div>

            {rate.reason && (
              <div className="mt-3 pt-3 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  <span className="font-semibold">Reason:</span> {rate.reason}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Request Adjustment Button (if whitelisted OR has rejected requests) */}
      {(isWhitelisted || hasRejectedRequest) && (
        <button
          onClick={onRequestAdjustment}
          className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg flex items-center justify-center gap-2 transition-all transform hover:scale-105"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
          {hasRejectedRequest && !isWhitelisted ? 'Edit & Resubmit Rate Request' : 'Request Rate Adjustment'}
        </button>
      )}

      {/* Request Stats */}
      {requestStats && (requestStats.total_requests > 0) && (
        <div className="mt-4 bg-white rounded-lg p-4 shadow-sm">
          <p className="text-sm text-gray-600 mb-2 font-semibold">Request History</p>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-gray-700">{requestStats.total_requests}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Approved</p>
              <p className="text-lg font-bold text-green-600">{requestStats.approved_requests}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Rejected</p>
              <p className="text-lg font-bold text-red-600">{requestStats.rejected_requests}</p>
            </div>
          </div>
        </div>
      )}

      {/* Historical Rates (collapsible) */}
      {historicalRates.length > 0 && (
        <div className="mt-4">
          <button
            onClick={() => setExpandHistory(!expandHistory)}
            className="w-full text-left text-sm text-gray-600 hover:text-gray-800 font-semibold flex items-center justify-between py-2"
          >
            <span>View Rate History ({historicalRates.length} past rates)</span>
            <svg
              className={`w-5 h-5 transform transition-transform ${expandHistory ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {expandHistory && (
            <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
              {historicalRates.map((rate) => (
                <div key={rate.id} className="bg-gray-50 rounded p-3 text-sm">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-semibold text-gray-700">{rate.company}</span>
                    <span className="text-xs text-gray-500">
                      {formatDate(rate.effective_date)} - {formatDate(rate.end_date)}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">Check:</span> {formatCurrency(rate.check_rate)}
                    </div>
                    <div>
                      <span className="text-gray-500">Weekday:</span> {formatCurrency(rate.weekday_cash_rate)}
                    </div>
                    <div>
                      <span className="text-gray-500">Weekend:</span> {formatCurrency(rate.weekend_cash_rate)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PayRateCard;

